import React, { useState } from 'react';
import { Container, Stepper, Step, StepLabel, Box, Button, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


import StepOneRegisterClient from './steps/StepClientRegisterClient';
import StepPlantRegisterClient from './steps/StepPlantRegisterClient';
import StepRolRegisterClient from './steps/StepRolRegisterClient';
import StepUserRegisterClient from './steps/StepUserRegisterClient';
import StepVariablesRegisterClient from './steps/StepVariableRegisterClient';

// -------------------------- TIPOS --------------------------
interface ClientData {
    id: number;
    clientName: string;
    clientPhoneContact: string;
    clientEmail: string;
}

interface PlantData {
    id: number;
    plantName: string;
    clientPhoneContact: string;
    clientEmail: string;
    clientId: number;
    rolePermissions: any[];
}

interface RolesData {
    rolePermissions: any[];
    createdRoles: any[];
}

interface UserData {
    userName: string;
    email: string;
    fullName: string;
    phoneNumber: string;
    password: string;
    roles: any[];
}

interface FormDataState {
    clientData?: ClientData;
    plantData?: PlantData;
    rolesData?: RolesData;
    userData?: UserData;
    variablesData?: any;
    apiResponse?: any;
}

// -------------------------- Componente Principal --------------------------
const RegisterClient: React.FC = () => {
    // Hooks y estados 
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<FormDataState>({});

    // stepper
    const steps = ['Cliente', 'Planta', 'Roles y Permisos', 'Usuario (opcional)', 'Variables (opcional)'];

    // -------------------------- MANEJADORES DE NAVEGACIÓN --------------------------
    const handleStep1Complete = (clientData: any, apiResponse: any) => {

        // Crear el objeto clientData con la estructura esperada
        const completeClientData: ClientData = {
            id: apiResponse.id,
            clientName: clientData.clientName,
            clientPhoneContact: clientData.clientPhoneContact,
            clientEmail: clientData.clientEmail
        };

        // Guardar datos del cliente y respuesta de la API
        setFormData(prev => ({
            ...prev,
            clientData: completeClientData,
            apiResponse: apiResponse
        }));

        // Siguiente paso
        setActiveStep(prev => prev + 1);
    };

    const handleStep2Complete = (plantData: any, apiResponse: any) => {


        // Crear el objeto plantData 
        const completePlantData: PlantData = {
            id: apiResponse.id,
            plantName: plantData.plantName,
            clientPhoneContact: plantData.clientPhoneContact,
            clientEmail: plantData.clientEmail,
            clientId: plantData.clientId,
            rolePermissions: plantData.rolePermissions || []
        };

        // Guardar datos de la planta
        setFormData(prev => ({
            ...prev,
            plantData: completePlantData
        }));

        // Avanzar al siguiente paso
        setActiveStep(prev => prev + 1);
    };

    const handleStep3Complete = (rolesData: RolesData) => {
        

        // Guardar datos de roles
        setFormData(prev => ({
            ...prev,
            rolesData: rolesData
        }));

        // Avanzar al siguiente paso
        setActiveStep(prev => prev + 1);
    };

    const handleStep4Complete = (userData: UserData, apiResponse: any) => {
        // Guardar datos del usuario
        setFormData(prev => ({
            ...prev,
            userData: userData
        }));

        // Avanzar al siguiente paso
        setActiveStep(prev => prev + 1);
    };

    // Avanzar al siguiente paso sin datos de usuario
    const handleStep4Skip = () => {

        setActiveStep(prev => prev + 1);
    };

    const handleStep5Complete = (variablesData: any) => {
        
        // Guardar datos de variables
        setFormData(prev => ({
            ...prev,
            variablesData: variablesData
        }));

        // Llamar al completado final
        handleComplete();
    };

    // Retroceder al paso anterior
    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    // -------------------------- MANEJADOR DE COMPLETADO --------------------------
    const handleComplete = async () => {
        try {
            // Redirigir a la lista de clients después del éxito
            navigate('/clients');

        } catch (error) {
            console.error('Error en registro completo:', error);
        }
    };

    // -------------------------- RENDERIZADO DE CONTENIDO POR PASO --------------------------
    const getStepContent = (step: number) => {
        switch (step) {
            case 0:
                // Paso 1: Creación del Cliente
                return (
                    <StepOneRegisterClient
                        onNext={handleStep1Complete}
                        initialData={formData.clientData || {}}
                    />
                );
            case 1:
                // Paso 2: Creación de la Planta
                if (!formData.clientData) {
                    return (
                        <Box textAlign="center" py={8}>
                            <Typography variant="h5" color="error">
                                Error: No hay datos del cliente
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Por favor, complete primero el paso del cliente.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                sx={{ mt: 3 }}
                            >
                                Volver al Paso 1
                            </Button>
                        </Box>
                    );
                }

                return (
                    <StepPlantRegisterClient
                        onNext={handleStep2Complete}
                        clientData={formData.clientData}
                    />
                );
            case 2:
                // Paso 3: Roles y Permisos
                if (!formData.clientData || !formData.plantData) {
                    return (
                        <Box textAlign="center" py={8}>
                            <Typography variant="h5" color="error">
                                Error: Datos incompletos
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Por favor, complete primero los pasos del cliente y la planta.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                sx={{ mt: 3 }}
                            >
                                Volver al Paso Anterior
                            </Button>
                        </Box>
                    );
                }

                return (
                    <StepRolRegisterClient
                        onNext={handleStep3Complete}
                        clientData={formData.clientData}
                        plantData={formData.plantData}
                    />
                );
            case 3:
                // Paso 4: Usuario
                if (!formData.clientData || !formData.plantData) {
                    return (
                        <Box textAlign="center" py={8}>
                            <Typography variant="h5" color="error">
                                Error: Datos incompletos
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Por favor, complete primero los pasos del cliente y la planta.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                sx={{ mt: 3 }}
                            >
                                Volver al Paso Anterior
                            </Button>
                        </Box>
                    );
                }

                return (
                    <StepUserRegisterClient
                        onNext={handleStep4Complete}
                        onSkip={handleStep4Skip}
                        clientData={formData.clientData}
                        plantData={formData.plantData}
                    />
                );
            case 4:
                // Paso 5: Variables
                if (!formData.clientData || !formData.plantData) {
                    return (
                        <Box textAlign="center" py={8}>
                            <Typography variant="h5" color="error">
                                Error: Datos incompletos
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Por favor, complete primero los pasos del cliente y la planta.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                sx={{ mt: 3 }}
                            >
                                Volver al Paso Anterior
                            </Button>
                        </Box>
                    );
                }

                return (
                    <StepVariablesRegisterClient
                        onNext={handleStep5Complete} 
                        onBack={handleBack}
                        clientData={formData.clientData}
                        plantData={formData.plantData}
                    />
                );
            default:
                return (
                    <Box textAlign="center" py={8}>
                        <Typography variant="h5" color="error">
                            Paso no válido
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => setActiveStep(0)}
                            sx={{ mt: 3 }}
                        >
                            Volver al inicio
                        </Button>
                    </Box>
                );
        }
    };

    // -------------------------- RENDERIZADO PRINCIPAL --------------------------
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Botón Volver al Dashboard */}
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ mb: 3 }}
            >
                Volver al Dashboard
            </Button>

            {/* Título principal */}
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
                Registro de Nuevo Cliente
            </Typography>

            {/* Descripción */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Complete la información en {steps.length} pasos para registrar un nuevo cliente en el sistema.
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>
                            <Typography variant="body2" fontWeight={activeStep === index ? 'bold' : 'normal'}>
                                {label}
                            </Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Contenido del paso actual */}
            <Box sx={{
                minHeight: 400,
                padding: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper'
            }}>
                {getStepContent(activeStep)}
            </Box>

        </Container>
    );
};

export default RegisterClient;